import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Search, TriangleAlert, X } from 'lucide-react';

export const Button = ({ variant = 'primary', icon: Icon, children, className = '', ...rest }) => (
  <button type="button" className={`adm-btn adm-btn-${variant} ${className}`} {...rest}>
    {Icon && <Icon size={16} strokeWidth={2.25} aria-hidden />}
    {children}
  </button>
);

export const Spinner = () => <span className="adm-spinner" aria-label="Laden" />;

export const Field = ({ label, htmlFor, children, className = '' }) => (
  <div className={`adm-field ${className}`}>
    <label className="adm-label" htmlFor={htmlFor}>
      {label}
    </label>
    {children}
  </div>
);

export const MoneyInput = (props) => (
  <div className="adm-money">
    <span aria-hidden>€</span>
    <input className="adm-input" type="text" inputMode="decimal" autoComplete="off" {...props} />
  </div>
);

export const FormError = ({ children }) =>
  children ? (
    <div className="adm-form-error" role="alert">
      <TriangleAlert size={16} aria-hidden />
      <span>{children}</span>
    </div>
  ) : null;

export const Segmented = ({ label, options, value, onChange }) => {
  const name = useId();
  return (
    <div className="adm-segment" role="radiogroup" aria-label={label}>
      {options.map((option) => (
        <label key={option.value} className={option.value === value ? 'is-active' : undefined}>
          <input
            type="radio"
            name={name}
            checked={option.value === value}
            onChange={() => onChange(option.value)}
          />
          {option.label}
        </label>
      ))}
    </div>
  );
};

const avatarColors = ['#14476a', '#2884c5', '#5c8fb5', '#3d6d92', '#7fb3dc', '#1b5c8c'];

export const Avatar = ({ name = '' }) => {
  const code = [...name].reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return (
    <span className="adm-avatar" style={{ background: avatarColors[code % avatarColors.length] }} aria-hidden>
      {name.trim().charAt(0).toUpperCase() || '?'}
    </span>
  );
};

export const SearchInput = ({ value, onChange, placeholder = 'Zoeken' }) => (
  <label className="adm-search">
    <Search size={15} aria-hidden />
    <input
      type="search"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      aria-label={placeholder}
    />
  </label>
);

export const MonthSwitcher = ({ month, onChange }) => (
  <div className="adm-month">
    <button type="button" onClick={() => onChange(month.subtract(1, 'month'))} aria-label="Vorige maand">
      <ChevronLeft size={16} />
    </button>
    <span className="adm-month-name">{month.locale('nl').format('MMMM YYYY')}</span>
    <button type="button" onClick={() => onChange(month.add(1, 'month'))} aria-label="Volgende maand">
      <ChevronRight size={16} />
    </button>
  </div>
);

export const EntryCard = ({ title, inline, onSubmit, onCancel, busy, children, actions }) => {
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;

  useEffect(() => {
    if (!inline || busy) return undefined;
    const onKey = (event) => {
      if (event.key !== 'Escape' || document.querySelector('.adm-overlay')) return;
      cancelRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [inline, busy]);

  return (
    <form
      className={`adm-entry${inline ? ' is-inline' : ''}`}
      autoComplete="off"
      onSubmit={(event) => {
        event.preventDefault();
        if (!busy) onSubmit(event);
      }}
    >
      <div className="adm-entry-head">
        <h2 className="adm-entry-title">{title}</h2>
        {inline && (
          <button type="button" className="adm-icon-btn" onClick={onCancel} disabled={busy} aria-label="Dichtklappen">
            <X size={16} />
          </button>
        )}
      </div>
      {children}
      {actions && <div className="adm-entry-actions">{actions}</div>}
    </form>
  );
};

export const Modal = ({ title, onClose, footer, size = 'sm', children }) => {
  const titleId = useId();
  const panelRef = useRef(null);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (!panelRef.current?.contains(document.activeElement)) panelRef.current?.focus();
  }, []);

  return (
    <div
      className="adm-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className={`adm-modal is-${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="adm-modal-head">
          <h2 id={titleId} className="adm-modal-title">
            {title}
          </h2>
          <button type="button" className="adm-icon-btn" onClick={onClose} aria-label="Sluiten">
            <X size={18} />
          </button>
        </div>
        <div className="adm-modal-body">{children}</div>
        {footer && <div className="adm-modal-foot">{footer}</div>}
      </div>
    </div>
  );
};

export const ConfirmDialog = ({ title, text, confirmLabel = 'Verwijderen', onClose }) => (
  <Modal
    title={title}
    onClose={() => onClose(false)}
    footer={
      <>
        <Button variant="ghost" onClick={() => onClose(false)} autoFocus>
          Annuleren
        </Button>
        <Button variant="danger" onClick={() => onClose(true)}>
          {confirmLabel}
        </Button>
      </>
    }
  >
    <div className="adm-confirm">
      <span className="adm-confirm-icon">
        <TriangleAlert size={20} aria-hidden />
      </span>
      <p>{text}</p>
    </div>
  </Modal>
);

export const Combobox = ({ id, options, value, onChange, placeholder, disabled, autoFocus, inputRef }) => {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [active, setActive] = useState(0);
  const listRef = useRef(null);
  const selected = options.find((option) => option.value === value);

  const matches = useMemo(() => {
    const needle = text.trim().toLowerCase();
    return needle ? options.filter((option) => option.label.toLowerCase().includes(needle)) : options;
  }, [options, text]);

  useEffect(() => {
    if (open) listRef.current?.children[active]?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const openList = () => {
    setText('');
    setActive(Math.max(0, options.findIndex((option) => option.value === value)));
    setOpen(true);
  };

  const pick = (option) => {
    onChange(option.value);
    setOpen(false);
    setText('');
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        openList();
        return;
      }
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((index) => (index + step + matches.length) % Math.max(1, matches.length));
    } else if (event.key === 'Enter' && open) {
      event.preventDefault();
      if (matches[active]) pick(matches[active]);
    } else if (event.key === 'Escape' && open) {
      event.stopPropagation();
      setOpen(false);
      setText('');
    } else if (event.key === 'Tab' && open && text && matches[active]) {
      pick(matches[active]);
    }
  };

  return (
    <div className={`adm-combo${open ? ' is-open' : ''}`}>
      <input
        id={id}
        ref={inputRef}
        className="adm-input"
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        autoComplete="off"
        value={open ? text : selected?.label || ''}
        placeholder={open && selected ? selected.label : placeholder}
        onChange={(event) => {
          setText(event.target.value);
          setActive(0);
          if (!open) setOpen(true);
        }}
        onClick={() => !open && openList()}
        onBlur={() => {
          setOpen(false);
          setText('');
        }}
        onKeyDown={onKeyDown}
        disabled={disabled}
        autoFocus={autoFocus}
      />
      <ChevronDown size={16} className="adm-combo-caret" aria-hidden />
      {open && (
        <ul id={listId} ref={listRef} className="adm-combo-list" role="listbox">
          {matches.length === 0 ? (
            <li className="adm-combo-none">Niets gevonden</li>
          ) : (
            matches.map((option, index) => (
              <li
                key={option.value}
                role="option"
                aria-selected={option.value === value}
                className={`${index === active ? 'is-active' : ''}${option.value === value ? ' is-selected' : ''}`}
                onMouseDown={(event) => {
                  event.preventDefault();
                  pick(option);
                }}
                onMouseEnter={() => setActive(index)}
              >
                {option.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

const freshMs = 2400;

export const useFreshIds = (rows, getId) => {
  const seen = useRef(null);
  const timer = useRef(null);
  const getIdRef = useRef(getId);
  getIdRef.current = getId;
  const [fresh, setFresh] = useState(() => new Set());

  useEffect(() => {
    if (seen.current === null && rows.length === 0) return;
    const ids = rows.map((row) => getIdRef.current(row));
    if (seen.current === null) {
      seen.current = new Set(ids);
      return;
    }
    const added = ids.filter((id) => !seen.current.has(id));
    ids.forEach((id) => seen.current.add(id));
    if (added.length === 0) return;
    setFresh(new Set(added));
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setFresh(new Set()), freshMs);
  }, [rows]);

  useEffect(() => () => clearTimeout(timer.current), []);

  return fresh;
};

export const ListState = ({ loading, error, empty, onRetry, children }) => {
  if (loading) {
    return (
      <div className="adm-list-note">
        <Spinner />
      </div>
    );
  }
  if (error) {
    return (
      <div className="adm-list-note">
        Kon de gegevens niet laden.{' '}
        {onRetry && (
          <button type="button" className="adm-link" onClick={onRetry}>
            Opnieuw proberen
          </button>
        )}
      </div>
    );
  }
  if (empty) return <div className="adm-list-note">{empty}</div>;
  return children;
};
